import {
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Computer as ComputerIcon,
    Error as ErrorIcon,
    Home as HomeIcon,
    Memory as MemoryIcon,
    MonitorHeart as MonitorHeartIcon,
    NavigateNext as NavigateNextIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MonitoringService } from '../../services/MonitoringService';
import { PageHeader } from '../common/PageHeader';

const MonitoringDashboard = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false); // ADD THIS STATE
    const [lastUpdated, setLastUpdated] = useState(null); // ADD THIS STATE
    const navigate = useNavigate();

    const fetchData = async (isManualRefresh = false) => {
        try {
            if (isManualRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const data = await MonitoringService.getDashboardData();
            setDashboardData(data);
            setLastUpdated(new Date()); // SET LAST UPDATED TIME
            setError(null);
        } catch (err) {
            setError('Failed to fetch monitoring data');
            console.error('Monitoring error:', err);
        } finally {
            if (isManualRefresh) {
                setRefreshing(false);
            } else {
                setLoading(false);
            }
        }
    };

    const handleManualRefresh = () => {
        fetchData(true);
    };

    useEffect(() => {
        fetchData();

        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    const { threadPools, liveStreamHealth, performance, alerts, overallHealthScore, overallStatus } =
        dashboardData || {};

    // Add null checks for nested properties
    const systemInfo = threadPools?.systemInfo;
    const memoryUsagePercent = systemInfo?.memoryUsagePercent || 0;
    const availableProcessors = systemInfo?.availableProcessors || 0;

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
            }}
        >
            <Paper
                sx={{
                    borderRadius: 0,
                    padding: 0,
                }}
            >
                <PageHeader
                    darkMode={darkMode}
                    toggleDarkMode={toggleDarkMode}
                    themeVariant={themeVariant}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, padding: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, padding: 1 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<HomeIcon />}
                            onClick={() => navigate('/')}
                            sx={{ mr: 2 }}
                        >
                            Home
                        </Button>
                        {/* Add arrow icon between buttons */}
                        <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Button variant="text" color="secondary" startIcon={<MonitorHeartIcon />} sx={{ mr: 2 }}>
                            System
                        </Button>
                        <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Button variant="text" color="secondary" startIcon={<AssessmentIcon />} sx={{ mr: 2 }}>
                            System Monitoring
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {lastUpdated && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {lastUpdated.toLocaleTimeString()}
                            </Typography>
                        )}
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                            onClick={handleManualRefresh}
                            disabled={refreshing}
                            sx={{ minWidth: '120px' }}
                        >
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </Button>
                    </Box>
                </Box>

                {/* Overall Health Status */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography variant="h6">Overall System Health</Typography>
                                    <Chip
                                        label={overallStatus}
                                        color={
                                            overallStatus === 'HEALTHY'
                                                ? 'success'
                                                : overallStatus === 'WARNING'
                                                ? 'warning'
                                                : 'error'
                                        }
                                        icon={overallStatus === 'HEALTHY' ? <CheckCircleIcon /> : <WarningIcon />}
                                    />
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={overallHealthScore}
                                        color={
                                            overallStatus === 'HEALTHY'
                                                ? 'success'
                                                : overallStatus === 'WARNING'
                                                ? 'warning'
                                                : 'error'
                                        }
                                    />
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Health Score: {overallHealthScore}/100
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    <MemoryIcon sx={{ mr: 1 }} />
                                    System Resources
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2">
                                        Memory Usage: {memoryUsagePercent.toFixed(1)}%
                                    </Typography>
                                    <LinearProgress variant="determinate" value={memoryUsagePercent} sx={{ mt: 1 }} />
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Available Processors: {availableProcessors}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Alerts - Add null check */}
                {alerts?.hasAlerts && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom color="error">
                            <ErrorIcon sx={{ mr: 1 }} />
                            Active Alerts ({alerts.alertCount})
                        </Typography>
                        {alerts.activeAlerts?.map((alert, index) => (
                            <Alert key={index} severity={alert.severity.toLowerCase()} sx={{ mb: 1 }}>
                                <strong>{alert.type}:</strong> {alert.message}
                            </Alert>
                        ))}
                    </Box>
                )}

                {/* Thread Pool Status - Add null checks */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <ThreadPoolCard title="Main Async Thread Pool" poolData={threadPools?.mainAsyncPool} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ThreadPoolCard title="Translation Thread Pool" poolData={threadPools?.translationPool} />
                    </Grid>
                </Grid>

                {/* Live Stream Health - Add null checks */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            <ComputerIcon sx={{ mr: 1 }} />
                            Live Stream Processing ({liveStreamHealth?.totalActiveVideos || 0} active videos)
                        </Typography>
                        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mr: 2 }}>
                                Risk Level:
                            </Typography>
                            <Chip
                                label={liveStreamHealth?.riskLevel || 'UNKNOWN'}
                                color={
                                    liveStreamHealth?.riskLevel === 'LOW'
                                        ? 'success'
                                        : liveStreamHealth?.riskLevel === 'MEDIUM'
                                        ? 'warning'
                                        : 'error'
                                }
                                size="small"
                            />
                        </Box>

                        {liveStreamHealth?.activeVideos?.length > 0 && (
                            <Paper sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Video ID</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Processing Time</TableCell>
                                            <TableCell>Languages</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {liveStreamHealth.activeVideos.map((video) => (
                                            <TableRow key={video.videoId}>
                                                <TableCell>{video.videoId.substring(0, 8)}...</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={video.status}
                                                        size="small"
                                                        color={video.isDelayed ? 'error' : 'primary'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {video.processingTimeMinutes
                                                        ? `${video.processingTimeMinutes} min`
                                                        : 'N/A'}
                                                </TableCell>
                                                <TableCell>{video.languages?.length || 0} languages</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}
                    </CardContent>
                </Card>

                {/* Performance Metrics - Add null checks */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Translation Performance (Last 24h)
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={3}>
                                <Typography variant="body2" color="textSecondary">
                                    Average Time
                                </Typography>
                                <Typography variant="h6">
                                    {performance?.averageTranslationTimeHours
                                        ? `${performance.averageTranslationTimeHours.toFixed(1)}h`
                                        : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Typography variant="body2" color="textSecondary">
                                    Success Rate
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color={performance?.successRate > 90 ? 'success.main' : 'warning.main'}
                                >
                                    {performance?.successRate ? `${performance.successRate.toFixed(1)}%` : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Typography variant="body2" color="textSecondary">
                                    Total Translations
                                </Typography>
                                <Typography variant="h6">{performance?.totalTranslationsLast24h || 0}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Typography variant="body2" color="textSecondary">
                                    Failures
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color={performance?.failureCount > 0 ? 'error.main' : 'success.main'}
                                >
                                    {performance?.failureCount || 0}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Paper>
        </Box>
    );
};

const ThreadPoolCard = ({ title, poolData }) => {
    if (typeof poolData === 'string') {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {poolData}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">Utilization: {poolData.utilizationPercent.toFixed(1)}%</Typography>
                    <LinearProgress
                        variant="determinate"
                        value={poolData.utilizationPercent}
                        color={
                            poolData.utilizationPercent > 80
                                ? 'error'
                                : poolData.utilizationPercent > 60
                                ? 'warning'
                                : 'primary'
                        }
                        sx={{ mt: 1 }}
                    />
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                            Active
                        </Typography>
                        <Typography variant="h6">{poolData.activeCount}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                            Core Size
                        </Typography>
                        <Typography variant="h6">{poolData.corePoolSize}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                            Queue Size
                        </Typography>
                        <Typography variant="h6">{poolData.queueSize}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                            Completed
                        </Typography>
                        <Typography variant="h6">{poolData.completedTaskCount}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default MonitoringDashboard;
